'use client';

import 'BomdisoftComponents/console/styles.css';
import { IconButton } from 'BomdisoftComponents/buttons/icon';
import { orderGroups } from 'BomdisoftComponents/console/group-order';
import { NavGroup, type RowMarks } from 'BomdisoftComponents/console/nav-group';
import { MAX_INDENT_DEPTH, type NavRow } from 'BomdisoftComponents/console/nesting';
import { BottomSheet } from 'BomdisoftComponents/drawers/bottom-sheet';
import { useMediaQuery } from 'BomdisoftComponents/hooks/use-media-query';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { rowEnteredClassName } from 'BomdisoftComponents/row-entry/index';
import { Tooltip } from 'BomdisoftComponents/tooltip/index';
import { type AnimationEvent, type ElementType, Fragment, type MouseEvent, type ReactNode, useState } from 'react';

export type ConsoleNavEntry = {
  key: string;
  href: string;
  icon: ReactNode;
  label: string;
  /** Rows nested under this one. A rail passing any of these must also pass `nesting`. */
  children?: ConsoleNavEntry[];
  group?: string;
  /** Rendered at the row's end: an unread count chip, a status dot. */
  badge?: ReactNode;
  /** This row leaves the app. Draws a muted arrow at the row's end.
   *
   * Its own flag rather than an icon passed as `badge`: `badge` is a filled
   * primary count pill, so an arrow put through it renders as a solid blob
   * with a glyph crushed inside. Both ID consoles shipped that way. */
  external?: boolean;
  /** Rows that open an overlay instead of navigating call preventDefault here. */
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  note?: string;
  tone?: 'faded' | 'muted';
  disabled?: boolean;
};

export type ConsoleNavAction = Omit<ConsoleNavEntry, 'children' | 'href' | 'onClick'> & {
  children?: undefined;
  href?: undefined;
  onClick: (event: MouseEvent<HTMLElement>) => void;
};

export type ConsoleNavRow = ConsoleNavEntry | ConsoleNavAction;

/**
 * How a rail's nested rows fold.
 *
 * Optional, and absent it behaves the way a group does without `onToggleGroup`:
 * every nested row renders, indented, with no twisty to press. Rows are never
 * silently dropped for want of a label.
 */
const preventNavigation = (event: MouseEvent<HTMLElement>) => event.preventDefault();

export type ConsoleNavNesting = {
  /** Translated, and it changes with the row's state. */
  collapseLabel: string;
  expandLabel: string;
  /** Keys of the rows whose children are showing. The caller owns this, as `openGroups` is
   *  owned, because every console persists it differently. */
  openKeys: readonly string[];
  onToggle: (key: string) => void;
};

export type ConsoleNavGroupOptions = {
  actions?: ReactNode;
  footer?: ReactNode;
  loadingLabel?: string;
  notice?: ReactNode;
  pulseArrivals?: boolean;
  sidebarOnly?: boolean;
  tree?: boolean;
};

export type ConsoleNavProps = {
  activeKey?: string;
  brand: ReactNode;
  entries: ConsoleNavRow[];
  expanded: boolean;
  foot?: ReactNode;
  groupOrder?: readonly string[];
  groups?: Readonly<Record<string, ConsoleNavGroupOptions>>;
  /** Product-owned sections placed after the entry groups: a page tree, favourites, a channel
   * list. Rendered only while expanded, like `brand`. */
  sections?: ReactNode;
  /** What the rail belongs to, placed directly under the head and above the entries: a workspace,
   * a project, a scope. Unlike `brand` and `sections` this renders at BOTH widths, because a
   * collapsed rail that cannot say what it is scoped to is a rail nobody can trust. The product
   * decides what fits each width. */
  scope?: ReactNode;
  groupLabels?: Record<string, string>;
  linkComponent?: ElementType;
  mobileVisibleCount?: number;
  moreLabel: string;
  nesting?: ConsoleNavNesting;
  /** Which groups are open. The caller owns this, as `CollapsibleBand` does: every console
   * persists it differently and a component that remembered internally would fight whichever
   * one it guessed wrong. Without `onToggleGroup` the nav renders as it always has. */
  openGroups?: string[];
  onToggleGroup?: (group: string) => void;
  onToggle: () => void;
  testId?: string;
  toggleCollapseLabel: string;
  toggleExpandLabel: string;
};

const PanelLeftGlyph = () => (
  <svg aria-hidden='true' fill='none' height='18' stroke='currentColor' strokeLinecap='round' strokeLinejoin='round' strokeWidth='1.7' viewBox='0 0 24 24' width='18'>
    <rect height='16' rx='2' width='18' x='3' y='4' />
    <path d='M9 4v16' />
  </svg>
);

const ExternalGlyph = () => (
  <svg aria-hidden='true' fill='none' height='14' stroke='currentColor' strokeLinecap='round' strokeLinejoin='round' strokeWidth='1.8' viewBox='0 0 24 24' width='14'>
    <path d='M14 4h6v6' />
    <path d='M20 4l-8.5 8.5' />
    <path d='M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5' />
  </svg>
);

const CaretGlyph = () => (
  <svg aria-hidden='true' fill='none' height='14' stroke='currentColor' strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' viewBox='0 0 24 24' width='14'>
    <path d='M9 6l6 6-6 6' />
  </svg>
);

const MoreGlyph = () => (
  <svg aria-hidden='true' fill='currentColor' height='18' viewBox='0 0 24 24' width='18'>
    <circle cx='5' cy='12' r='1.6' />
    <circle cx='12' cy='12' r='1.6' />
    <circle cx='19' cy='12' r='1.6' />
  </svg>
);

/* Below this width the layout drops its `data-nav='left'` rules and the same element is the
   full-width bar, whose groups are `display: contents` with their titles hidden. Sections are a
   sidebar affordance; the bar has none to press, so it renders every entry exactly as before. */
const SIDEBAR_WIDTH_QUERY = '(min-width: 770px)';

const NO_OPTIONS: ConsoleNavGroupOptions = {};

export const ConsoleNav = ({
  activeKey,
  brand,
  entries,
  expanded,
  foot,
  groupOrder = [],
  groups = {},
  sections,
  scope,
  groupLabels = {},
  linkComponent: LinkComponent = 'a',
  mobileVisibleCount = 4,
  moreLabel,
  nesting,
  openGroups,
  onToggleGroup,
  onToggle,
  testId = 'console-nav',
  toggleCollapseLabel,
  toggleExpandLabel,
}: ConsoleNavProps) => {
  const [moreOpen, setMoreOpen] = useState(false);
  const sidebarWidth = useMediaQuery(SIDEBAR_WIDTH_QUERY, true);

  const wide = expanded && sidebarWidth;
  const optionsFor = (group: string | undefined): ConsoleNavGroupOptions => (group === undefined ? NO_OPTIONS : (groups[group] ?? NO_OPTIONS));
  const order = orderGroups(
    entries,
    groupOrder.filter(group => groups[group] !== undefined || entries.some(entry => entry.group === group)),
  );

  const ordered = order.filter(group => optionsFor(group).sidebarOnly !== true).flatMap(group => entries.filter(entry => entry.group === group));
  const overflow = ordered.length > mobileVisibleCount + 1 ? ordered.slice(mobileVisibleCount) : [];
  const isOverflow = (entry: ConsoleNavRow) => overflow.includes(entry);
  const toggleLabel = expanded ? toggleCollapseLabel : toggleExpandLabel;

  const openKeys: ReadonlySet<string> | 'all' = nesting === undefined ? 'all' : new Set(nesting.openKeys);

  const renderRow = ({ depth, entry, hasChildren }: NavRow<ConsoleNavRow>, { entered, onEntryEnd, twistyColumn }: RowMarks) => {
    const RowElement = entry.href === undefined ? 'button' : LinkComponent;
    const link = (
      <RowElement
        aria-disabled={entry.disabled ? 'true' : undefined}
        className={joinClasses('bomdisoft-console-nav-item', entered && rowEnteredClassName)}
        data-active={entry.key === activeKey ? '' : undefined}
        data-depth={Math.min(depth, MAX_INDENT_DEPTH)}
        data-disabled={entry.disabled ? '' : undefined}
        data-entered={entered ? '' : undefined}
        data-key={entry.key}
        data-mobile-overflow={isOverflow(entry) ? '' : undefined}
        data-testid={`${testId}-${entry.key}`}
        data-tone={entry.tone}
        href={entry.href}
        onAnimationEnd={(event: AnimationEvent<HTMLElement>) => {
          if (event.target === event.currentTarget) onEntryEnd();
        }}
        onClick={entry.disabled ? preventNavigation : entry.onClick}
        tabIndex={entry.disabled ? -1 : undefined}
        type={entry.href === undefined ? 'button' : undefined}
      >
        <span aria-hidden='true' className='bomdisoft-console-nav-icon'>
          {entry.icon}
        </span>
        <span className='bomdisoft-console-nav-label'>{entry.label}</span>
        {entry.note ? (
          <span className='bomdisoft-console-nav-note' data-testid={`${testId}-${entry.key}-note`}>
            {entry.note}
          </span>
        ) : null}
        {entry.external ? (
          <span className='bomdisoft-console-nav-external'>
            <ExternalGlyph />
          </span>
        ) : null}
        {entry.badge ? <span className='bomdisoft-console-nav-badge'>{entry.badge}</span> : null}
      </RowElement>
    );

    const labelled = expanded ? (
      link
    ) : (
      <Tooltip placement='right' title={entry.label}>
        {link}
      </Tooltip>
    );

    if (nesting === undefined || !twistyColumn) return <Fragment key={entry.key}>{labelled}</Fragment>;

    const open = openKeys === 'all' || openKeys.has(entry.key);
    const twistyLabel = open ? nesting.collapseLabel : nesting.expandLabel;

    return (
      <div className='bomdisoft-console-nav-row' key={entry.key}>
        {hasChildren ? (
          <Tooltip placement='right' title={twistyLabel}>
            <button aria-expanded={open} aria-label={twistyLabel} className='bomdisoft-console-nav-twisty' data-key={entry.key} data-open={open ? '' : undefined} data-testid={`${testId}-twisty-${entry.key}`} onClick={() => nesting.onToggle(entry.key)} type='button'>
              <CaretGlyph />
            </button>
          </Tooltip>
        ) : (
          <span className='bomdisoft-console-nav-twisty' data-leaf='' />
        )}
        {labelled}
      </div>
    );
  };

  return (
    <nav className='bomdisoft-console-nav-nav' data-expanded={expanded ? '' : undefined} data-testid={testId}>
      <div className='bomdisoft-console-nav-head'>
        {expanded ? <span className='bomdisoft-console-nav-brand'>{brand}</span> : null}
        <span className='bomdisoft-console-nav-spacer' />
        <Tooltip placement='right' title={toggleLabel}>
          <IconButton aria-label={toggleLabel} data-testid={`${testId}-toggle`} onClick={onToggle} size='small'>
            <PanelLeftGlyph />
          </IconButton>
        </Tooltip>
      </div>

      {scope ? <div className='bomdisoft-console-nav-scope'>{scope}</div> : null}

      {order.map(group => {
        const options = optionsFor(group);
        if (options.sidebarOnly === true && !wide) return null;
        const groupEntries = entries.filter(entry => entry.group === group);
        const label = group ? groupLabels[group] : undefined;
        const toggle = onToggleGroup;
        const foldable = group !== undefined && Boolean(label) && toggle !== undefined && options.actions === undefined && wide;
        const open = foldable ? (openGroups ?? []).includes(group) : true;
        const marked = !open && activeKey !== undefined && groupEntries.some(entry => entry.key === activeKey);

        const head =
          foldable && group !== undefined && toggle !== undefined ? (
            <button aria-expanded={open} className='bomdisoft-console-nav-group-head' data-active={marked ? '' : undefined} data-group={group} data-testid={`${testId}-group-${group}`} onClick={() => toggle(group)} type='button'>
              {label}
              <span className='bomdisoft-console-nav-group-spacer' />
              {marked ? <span aria-hidden='true' className='bomdisoft-console-nav-group-dot' /> : null}
              <span aria-hidden='true' className='bomdisoft-console-nav-caret' data-open={open ? '' : undefined} />
            </button>
          ) : options.actions !== undefined && wide ? (
            <div className='bomdisoft-console-nav-group-title-row'>
              {label ? <p className='bomdisoft-console-nav-group-title'>{label}</p> : null}
              <span className='bomdisoft-console-nav-group-actions'>{options.actions}</span>
            </div>
          ) : label ? (
            <p className='bomdisoft-console-nav-group-title'>{label}</p>
          ) : null;

        return <NavGroup entries={groupEntries} head={head} key={group ?? 'ungrouped'} loadingTestId={`${testId}-group-${group ?? 'ungrouped'}-loading`} open={open} openKeys={openKeys} options={options} renderRow={renderRow} testId={`${testId}-groupbox-${group ?? 'ungrouped'}`} wide={wide} />;
      })}

      {/* Expanded only, for the same reason `brand` is: at the collapsed width the toggle is the
          only control that fits, and a section of tree rows there would push it out of reach. */}
      {expanded && sections ? <div className='bomdisoft-console-nav-sections'>{sections}</div> : null}

      {overflow.length > 0 ? (
        <>
          <Tooltip placement='top' title={moreLabel}>
            <button aria-label={moreLabel} className='bomdisoft-console-nav-more' data-testid={`${testId}-more`} onClick={() => setMoreOpen(true)} type='button'>
              <MoreGlyph />
            </button>
          </Tooltip>
          <BottomSheet label={moreLabel} onClose={() => setMoreOpen(false)} open={moreOpen} testId={`${testId}-sheet`}>
            {overflow.map(entry => {
              const SheetElement = entry.href === undefined ? 'button' : LinkComponent;
              return (
                <SheetElement
                  aria-disabled={entry.disabled ? 'true' : undefined}
                  className='bomdisoft-console-nav-sheet-item'
                  data-active={entry.key === activeKey ? '' : undefined}
                  data-disabled={entry.disabled ? '' : undefined}
                  data-key={entry.key}
                  data-testid={`${testId}-sheet-${entry.key}`}
                  data-tone={entry.tone}
                  href={entry.href}
                  key={entry.key}
                  onClick={(event: MouseEvent<HTMLElement>) => {
                    if (entry.disabled) {
                      preventNavigation(event);
                      return;
                    }
                    setMoreOpen(false);
                    entry.onClick?.(event);
                  }}
                  tabIndex={entry.disabled ? -1 : undefined}
                  type={entry.href === undefined ? 'button' : undefined}
                >
                  <span aria-hidden='true' className='bomdisoft-console-nav-icon'>
                    {entry.icon}
                  </span>
                  {entry.label}
                  {entry.note ? (
                    <span className='bomdisoft-console-nav-note' data-testid={`${testId}-sheet-${entry.key}-note`}>
                      {entry.note}
                    </span>
                  ) : null}
                  {entry.external ? (
                    <span className='bomdisoft-console-nav-external'>
                      <ExternalGlyph />
                    </span>
                  ) : null}
                  {entry.badge ? <span className='bomdisoft-console-nav-badge'>{entry.badge}</span> : null}
                </SheetElement>
              );
            })}
          </BottomSheet>
        </>
      ) : null}

      {foot ? <div className='bomdisoft-console-nav-foot'>{foot}</div> : null}
    </nav>
  );
};
